import React, {
  FC,
  useEffect, useRef, useState,
} from 'react';
import cx from 'classnames';
import Swiper from 'swiper';

import { IconComponentFunction } from '@motorway/mw-highway-code';

import { GA_TAGS } from 'Utilities/analytics';
import {
  REVIEW_BODY_MAX_LENGTH,
  REVIEW_TITLE_MAX_LENGTH,
} from 'Utilities/helpers';

import { useBreakpoint } from '../../../../components/misc/Breakpoints/Breakpoints';
import useSwiperCarousel from '../swiper/useSwiperCarousel';

import { TrustpilotApiReview, TrustpilotBrand, TrustpilotCardProps, TrustpilotSliderConfig } from './Trustpilot.types';

type TrustpilotCommonCarouselProps = {
  PrevNextComponent: IconComponentFunction;
  TrustpilotCardComponent: FC<TrustpilotCardProps>;
  brand?: TrustpilotBrand;
  cardTitleTruncationLength?: number;
  cardTruncationLength?: number;
  generateBreakPointConfig: (breakpoints: Record<string, boolean>) => TrustpilotSliderConfig;
  minReviews?: number;
  reviews: TrustpilotApiReview[];
  styles: Record<string, string>;
};

export const TrustpilotCommonCarousel: FC<TrustpilotCommonCarouselProps> = ({
  brand,
  cardTitleTruncationLength = REVIEW_TITLE_MAX_LENGTH,
  cardTruncationLength = REVIEW_BODY_MAX_LENGTH,
  generateBreakPointConfig,
  minReviews = 4,
  PrevNextComponent,
  reviews,
  styles,
  TrustpilotCardComponent,
}) => {
  const [, setDomLoaded] = useState(false);

  const { maxWidth: breakpoints } = useBreakpoint();

  const $carousel = useRef<HTMLDivElement>(null);
  const $carouselNext = useRef<HTMLDivElement>(null);
  const $carouselPrev = useRef<HTMLDivElement>(null);

  const swiperConfig = generateBreakPointConfig(breakpoints);
  const reviewsLength = reviews?.length || 0;

  const enableSwiper = (reviewsLength > minReviews);
  const hideSwiperButtons = (reviewsLength <= swiperConfig?.slidesPerView);

  const swiperCarousel = useSwiperCarousel({
    config: {
      $el: $carousel.current,
      navigation: {
        nextEl: $carouselNext.current,
        prevEl: $carouselPrev.current,
      },
      on: {
        slideNextTransitionStart: () => {
          GA_TAGS.TRUSTPILOT_CAROUSEL_CLICKED('Next');
        },
        slidePrevTransitionStart: () => {
          GA_TAGS.TRUSTPILOT_CAROUSEL_CLICKED('Previous');
        },
      },
      ...swiperConfig,
    },
    enableSwiper,
  }) as Swiper | undefined;

  useEffect(() => {
    setDomLoaded(true); // Trigger a mount re-render
  }, []);

  useEffect(() => {
    if (swiperCarousel && !swiperCarousel.destroyed) {
      const { params } = swiperCarousel;
      Object.assign(params, generateBreakPointConfig(breakpoints));
      swiperCarousel.update();
    }
  }, [breakpoints, generateBreakPointConfig, swiperCarousel]);

  useEffect(() => {
    if (swiperCarousel && !swiperCarousel.destroyed) {
      swiperCarousel.update();
    }
  }, [reviews, swiperCarousel]);

  // Can't add dynamic nav buttons on swiper v6, so have to init when ready with all the data!
  // https://github.com/nolimits4web/swiper/issues/4681
  const nextPrevClassNames = (vector: string) => cx(`swiper-button-${vector}`, styles[`${vector}Button`], { [styles.hide]: !enableSwiper || hideSwiperButtons });

  return (
    <div className={styles.component}>
      <div
        ref={$carousel}
        className={cx({
          swiper: enableSwiper,
          [styles.carousel]: enableSwiper,
          [styles.noCarousel]: !enableSwiper,
        })}
      >
        <div className={cx({
          [styles.noCarouselWrapper]: !enableSwiper,
          'swiper-wrapper': enableSwiper,
          [styles.wrapperCenter]: enableSwiper && hideSwiperButtons,
        })}
        >
          {
            reviews.map((review) => (
              <div key={review.timestamp} className={cx(styles.carouselItem, { 'swiper-slide': enableSwiper })}>
                <TrustpilotCardComponent {...{
                  brand,
                  cardTitleTruncationLength,
                  cardTruncationLength,
                  review,
                }}
                />
              </div>
            ))
          }
        </div>
      </div>
      <div ref={$carouselPrev} className={nextPrevClassNames('prev')}>
        <PrevNextComponent />
      </div>
      <div ref={$carouselNext} className={nextPrevClassNames('next')}>
        <PrevNextComponent />
      </div>
    </div>
  );
};
